import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { InfoCard, Link, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { CopilotIcon } from '../CopilotIcon';
import {
  GuardrailInfo,
  GroupComplianceDetails,
  UserCopilotEligibility,
} from '../GuardrailComplianceTable/types';

const useStyles = makeStyles(theme => ({
  summaryCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  summaryStats: {
    display: 'flex',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  eligibleValue: {
    color: theme.palette.success.main,
  },
  ineligibleValue: {
    color: theme.palette.error.main,
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
    paddingRight: theme.spacing(2),
  },
  nameSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  metricSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  contextRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  groupLink: {
    fontWeight: 600,
  },
  guardrailList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  guardrailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  guardrailIcon: {
    fontSize: '1rem',
    marginTop: 2,
    flexShrink: 0,
  },
  compliantIcon: {
    color: theme.palette.success.main,
  },
  nonCompliantIcon: {
    color: theme.palette.error.main,
  },
  noMeasureIcon: {
    color: theme.palette.grey[500],
  },
  guardrailContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  guardrailTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  copilotIcon: {
    color: theme.palette.info.main,
    fontSize: '1rem',
    flexShrink: 0,
  },
  filteredCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  details: {
    color: theme.palette.text.secondary,
  },
  championList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  championRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  championStatusIcon: {
    fontSize: '1rem',
    marginTop: 2,
    flexShrink: 0,
  },
  championAssignedIcon: {
    color: theme.palette.success.main,
  },
  championMissingIcon: {
    color: theme.palette.error.main,
  },
  championText: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  championValue: {
    color: theme.palette.text.secondary,
  },
}));

const COPILOT_LICENSE_CHAMPIONS = new Set(['DevOps', 'Security']);

const CHAMPION_ORDER = [
  'DevOps',
  'Security',
  'Architecture',
  'Operations',
  'QA',
] as const;

type ChampionName = (typeof CHAMPION_ORDER)[number];

type ChampionEntry = {
  label: ChampionName;
  assigned: boolean;
  value?: string;
};

type GuardrailStatusState = 'compliant' | 'partial' | 'non-compliant' | 'no-measure';

const getGroupCatalogLink = (groupRef: string): string => {
  const [, refWithoutKind = groupRef] = groupRef.split(':');
  const [namespace, name] = refWithoutKind.includes('/')
    ? refWithoutKind.split('/')
    : ['default', refWithoutKind];

  return `/catalog/${encodeURIComponent(namespace)}/group/${encodeURIComponent(name)}`;
};

const extractGuardrailId = (guardrailRef: string, fallbackName?: string): string => {
  return (guardrailRef.split('/').pop() || fallbackName || '').toLowerCase();
};

const isChampionGuardrail = (guardrail: GuardrailInfo): boolean => {
  return ['guardrail_overaching', 'guardrail-overaching'].includes(
    extractGuardrailId(guardrail.guardrailRef, guardrail.guardrailName),
  );
};


const getGuardrailStatusState = (guardrail: GuardrailInfo): GuardrailStatusState => {
  if (guardrail.hasNoMeasure) {
    return 'no-measure';
  }

  if (guardrail.isCompliant) {
    return 'compliant';
  }

  return 'non-compliant';
};

const parseChampionDetails = (details?: string): ChampionEntry[] => {
  const championMap = new Map<ChampionName, ChampionEntry>(
    CHAMPION_ORDER.map(label => [label, { label, assigned: false }]),
  );

  if (!details) {
    return Array.from(championMap.values());
  }

  const parseAssignedEntries = (section: string) => {
    section
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)
      .forEach(part => {
        const separatorIndex = part.indexOf(':');
        if (separatorIndex === -1) {
          return;
        }

        const label = part.slice(0, separatorIndex).trim() as ChampionName;
        const value = part.slice(separatorIndex + 1).trim();

        if (!championMap.has(label)) {
          return;
        }

        championMap.set(label, {
          label,
          assigned: Boolean(value),
          value: value || undefined,
        });
      });
  };

  details
    .split('|')
    .map(section => section.trim())
    .filter(Boolean)
    .forEach(section => {
      if (section.startsWith('Assigned:')) {
        parseAssignedEntries(section.replace(/^Assigned:/, '').trim());
        return;
      }

      if (section.startsWith('Missing:')) {
        section
          .replace(/^Missing:/, '')
          .split(',')
          .map(part => part.trim() as ChampionName)
          .filter(part => championMap.has(part))
          .forEach(label => {
            championMap.set(label, { label, assigned: false });
          });
        return;
      }

      parseAssignedEntries(section);
    });

  return Array.from(championMap.values());
};

const filterChampionEntriesForCopilot = (
  championEntries: ChampionEntry[],
  showCopilotOnly: boolean,
): ChampionEntry[] => {
  if (!showCopilotOnly) {
    return championEntries;
  }

  return championEntries.filter(entry => COPILOT_LICENSE_CHAMPIONS.has(entry.label));
};

const getDisplayStatusState = (
  guardrail: GuardrailInfo,
  showCopilotOnly: boolean,
): GuardrailStatusState => {
  if (guardrail.hasNoMeasure) {
    return 'no-measure';
  }

  if (showCopilotOnly && guardrail.isDsoRelevant) {
    return guardrail.isDsoCompliant ? 'compliant' : 'non-compliant';
  }

  return getGuardrailStatusState(guardrail);
};

const getGuardrailStatusLabel = (status: GuardrailStatusState): string => {
  if (status === 'no-measure') {
    return 'No information available';
  }

  if (status === 'compliant') {
    return 'Compliant';
  }

  if (status === 'partial') {
    return 'Partially compliant';
  }

  return 'Non-compliant';
};

const renderGuardrailStatusIcon = (
  status: GuardrailStatusState,
  classes: ReturnType<typeof useStyles>,
) => {
  if (status === 'no-measure') {
    return <HelpOutlineIcon className={`${classes.guardrailIcon} ${classes.noMeasureIcon}`} />;
  }

  if (status === 'compliant' || status === 'partial') {
    return <CheckCircleIcon className={`${classes.guardrailIcon} ${classes.compliantIcon}`} />;
  }

  return <CancelIcon className={`${classes.guardrailIcon} ${classes.nonCompliantIcon}`} />;
};

const getVisibleGuardrails = (
  group: GroupComplianceDetails,
): GuardrailInfo[] => {
  return group.guardrails.filter(guardrail => guardrail.isDsoRelevant);
};

const getGuardrailSummary = (guardrails: GuardrailInfo[]) => {
  const measuredGuardrails = guardrails.filter(
    guardrail => getDisplayStatusState(guardrail, true) !== 'no-measure',
  );
  const totalGuardrails = measuredGuardrails.length;
  const compliantCount = measuredGuardrails.filter(
    guardrail => getDisplayStatusState(guardrail, true) === 'compliant',
  ).length;

  return {
    totalGuardrails,
    compliantCount,
    compliancePercentage:
      totalGuardrails === 0 ? 0 : Math.round((compliantCount / totalGuardrails) * 100),
  };
};

const renderChampionDetails = (
  details: string | undefined,
  showCopilotOnly: boolean,
  classes: ReturnType<typeof useStyles>,
) => {
  const championEntries = filterChampionEntriesForCopilot(
    parseChampionDetails(details),
    showCopilotOnly,
  );
  const hasStructuredChampionData = championEntries.some(
    entry => entry.assigned || Boolean(entry.value),
  );

  if (!hasStructuredChampionData && details) {
    return (
      <Typography variant="body2" className={classes.details}>
        {details}
      </Typography>
    );
  }

  return (
    <Box className={classes.championList}>
      {championEntries.map(entry => (
        <Box key={entry.label} className={classes.championRow}>
          {entry.assigned ? (
            <CheckCircleIcon
              className={`${classes.championStatusIcon} ${classes.championAssignedIcon}`}
            />
          ) : (
            <CancelIcon
              className={`${classes.championStatusIcon} ${classes.championMissingIcon}`}
            />
          )}
          <Box className={classes.championText}>
            <Typography variant="body2" component="span">
              <strong>{entry.label}</strong>
            </Typography>
            {COPILOT_LICENSE_CHAMPIONS.has(entry.label) && (
              <Tooltip title="Required for GitHub Copilot licensing" arrow>
                <span>
                  <CopilotIcon className={classes.copilotIcon} />
                </span>
              </Tooltip>
            )}
            <Typography variant="body2" component="span" className={classes.championValue}>
              {entry.assigned ? entry.value : 'Missing'}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const renderGroupGuardrails = (
  guardrails: GuardrailInfo[],
  showCopilotOnly: boolean,
  classes: ReturnType<typeof useStyles>,
) => {
  if (guardrails.length === 0) {
    return (
      <Typography>
        {showCopilotOnly
          ? 'No GitHub Copilot-relevant guardrails returned for this group'
          : 'No guardrail details returned for this group'}
      </Typography>
    );
  }

  return (
    <Box className={classes.guardrailList}>
      {guardrails.map(guardrail => {
        const statusState = getDisplayStatusState(guardrail, showCopilotOnly);

        return (
        <Box key={guardrail.guardrailRef} className={classes.guardrailRow}>
          {renderGuardrailStatusIcon(statusState, classes)}
          <Box className={classes.guardrailContent}>
            <Box className={classes.guardrailTitle}>
              <Typography variant="body2" component="span">
                <strong>{guardrail.guardrailName}</strong>
              </Typography>
                {guardrail.isDsoRelevant && (
                <Tooltip title="Relevant for GitHub Copilot licensing" arrow>
                  <span>
                    <CopilotIcon className={classes.copilotIcon} />
                  </span>
                </Tooltip>
              )}
              {guardrail.severity && (
                <Chip label={guardrail.severity.toUpperCase()} size="small" />
              )}
            </Box>
            <Typography variant="body2">{getGuardrailStatusLabel(statusState)}</Typography>
            {guardrail.details && (
              isChampionGuardrail(guardrail) ? (
                renderChampionDetails(guardrail.details, showCopilotOnly, classes)
              ) : (
                <Typography variant="body2" className={classes.details}>
                  {guardrail.details}
                </Typography>
              )
            )}
          </Box>
        </Box>
        );
      })}
    </Box>
  );
};

export const UserCopilotComplianceCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const { fetch } = useApi(fetchApiRef);
  const userName = entity.metadata.name;

  const { value, loading, error } = useAsync(
    async (): Promise<UserCopilotEligibility | null> => {
      const baseUrl = await discoveryApi.getBaseUrl('guardrail-compliance');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(
        `${baseUrl}/v1/compliance/users/${encodeURIComponent(userName)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch user compliance data: ${response.statusText}`);
      }

      return response.json();
    },
    [userName],
  );

  if (loading) {
    return (
      <InfoCard title="GitHub Copilot Eligibility">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="GitHub Copilot Eligibility">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  if (!value) {
    return (
      <InfoCard title="GitHub Copilot Eligibility">
        <Typography>No compliance data available for this user</Typography>
      </InfoCard>
    );
  }

  if (value.groups.length === 0) {
    return (
      <InfoCard title="GitHub Copilot Eligibility">
        <Box className={classes.summaryCard}>
          <div className={classes.summaryStats}>
            <div className={classes.stat}>
              <div
                className={`${classes.statValue} ${value.isEligibleForCopilot ? classes.eligibleValue : classes.ineligibleValue
                  }`}
              >
                {value.isEligibleForCopilot ? 'Eligible' : 'Not eligible'}
              </div>
              <div className={classes.statLabel}>License Eligibility</div>
            </div>
            <div className={classes.stat}>
              <div className={classes.statValue}>{value.groups.length}</div>
              <div className={classes.statLabel}>Member Groups</div>
            </div>
          </div>
        </Box>
        <Box className={classes.contextRow}>
          <Typography variant="body2" className={classes.filteredCount}>
            This user is not a member of any eligible group, so they are not eligible for a GitHub
            Copilot license.
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  const groupSummaries = value.groups.map(group => {
    const visibleGuardrails = getVisibleGuardrails(group);
    const summary = getGuardrailSummary(visibleGuardrails);

    return { group, visibleGuardrails, summary };
  });
  const visibleGuardrailCount = value.groups.reduce(
    (count, group) => count + getVisibleGuardrails(group).length,
    0,
  );

  return (
    <InfoCard title="GitHub Copilot Eligibility">
      <Box className={classes.summaryCard}>
        <div className={classes.summaryStats}>
          <div className={classes.stat}>
            <div
              className={`${classes.statValue} ${
                value.isEligibleForCopilot ? classes.eligibleValue : classes.ineligibleValue
              }`}
            >
              {value.isEligibleForCopilot ? 'Eligible' : 'Not eligible'}
            </div>
            <div className={classes.statLabel}>License Eligibility</div>
          </div>
          <div className={classes.stat}>
            <div className={classes.statValue}>{value.groups.length}</div>
            <div className={classes.statLabel}>Member Groups</div>
          </div>
        </div>
      </Box>

      <Box className={classes.contextRow}>
        <Typography variant="body2" className={classes.filteredCount}>
          Showing {visibleGuardrailCount} GitHub Copilot-relevant guardrails across member groups
        </Typography>
      </Box>

      {groupSummaries.map(({ group, visibleGuardrails, summary }) => {
        return (
        <Accordion key={group.groupRef} className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <div className={classes.summaryContent}>
              <div className={classes.nameSection}>
                <Link to={getGroupCatalogLink(group.groupRef)} className={classes.groupLink}>
                  {group.groupName}
                </Link>
                  {group.teamTopology && (
                    <Chip label={group.teamTopology} size="small" />
                  )}
                  {group.exemptionCategories?.map(category => (
                    <Chip key={category} label={category} size="small" />
                  ))}
                  {group.isEligibleForCopilot && (
                  <Chip label="Eligible group" size="small" color="primary" />
                )}
              </div>
              <div className={classes.metricSection}>
                {summary.totalGuardrails > 0 ? (
                  <>
                    <Typography variant="body2">{summary.compliancePercentage}% compliant</Typography>
                    <Typography variant="body2">
                      {summary.compliantCount}/{summary.totalGuardrails} guardrails
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">
                    {group.compliancePercentage === 100
                      ? 'No Copilot guardrails required'
                      : 'No Copilot-relevant guardrails'}
                  </Typography>
                )}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {visibleGuardrails.length === 0 && group.compliancePercentage === 100 ? (
              <Typography>No GitHub Copilot guardrails required for this group</Typography>
            ) : (
              renderGroupGuardrails(visibleGuardrails, true, classes)
            )}
          </AccordionDetails>
        </Accordion>
        );
      })}
    </InfoCard>
  );
};